import { useState } from 'react';

const months = ['Oct 2025', 'Nov 2025', 'Dec 2025', 'Jan 2026', 'Feb 2026', 'Mar 2026'];

const monthlyData = [
  { month: 'Oct 2025', rent: 8500, fees: 850, maintenance: 320, net: 7330, status: 'Paid' },
  { month: 'Nov 2025', rent: 8500, fees: 850, maintenance: 0, net: 7650, status: 'Paid' },
  { month: 'Dec 2025', rent: 8500, fees: 850, maintenance: 1200, net: 6450, status: 'Paid' },
  { month: 'Jan 2026', rent: 8500, fees: 850, maintenance: 0, net: 7650, status: 'Paid' },
  { month: 'Feb 2026', rent: 8500, fees: 850, maintenance: 450, net: 7200, status: 'Paid' },
  { month: 'Mar 2026', rent: 8500, fees: 850, maintenance: 0, net: 7650, status: 'Paid' },
];

const maxNet = Math.max(...monthlyData.map((d) => d.net));

export default function FinancialsTab() {
  const [selectedMonth, setSelectedMonth] = useState('Mar 2026');
  const selected = monthlyData.find((d) => d.month === selectedMonth) || monthlyData[5];

  const ytdRent = monthlyData.reduce((s, d) => s + d.rent, 0);
  const ytdFees = monthlyData.reduce((s, d) => s + d.fees, 0);
  const ytdMaint = monthlyData.reduce((s, d) => s + d.maintenance, 0);
  const ytdNet = monthlyData.reduce((s, d) => s + d.net, 0);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Financial Reports
        </h2>
        <p className="text-sm text-[#6B6B6B] font-light mt-1">October 2025 – March 2026</p>
      </div>

      {/* YTD Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Rent Collected', value: `$${ytdRent.toLocaleString()}`, icon: 'ri-money-dollar-circle-line', color: 'text-emerald-600' },
          { label: 'Management Fees', value: `$${ytdFees.toLocaleString()}`, icon: 'ri-percent-line', color: 'text-[#8B7355]' },
          { label: 'Maintenance Costs', value: `$${ytdMaint.toLocaleString()}`, icon: 'ri-tools-line', color: 'text-amber-600' },
          { label: 'Net Income', value: `$${ytdNet.toLocaleString()}`, icon: 'ri-bar-chart-2-line', color: 'text-[#1A1A1A]' },
        ].map((item) => (
          <div key={item.label} className="bg-white border border-[#E8E6E3] rounded-lg p-5">
            <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#F5F3F0] mb-3">
              <i className={`${item.icon} ${item.color} text-sm`}></i>
            </div>
            <p className="text-xl font-semibold text-[#1A1A1A]">{item.value}</p>
            <p className="text-xs text-[#9A9A9A] mt-1">{item.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar Chart */}
        <div className="lg:col-span-2 bg-white border border-[#E8E6E3] rounded-lg p-6">
          <h3 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-6">Monthly Net Income</h3>
          <div className="flex items-end gap-3 h-44">
            {monthlyData.map((d) => {
              const heightPct = Math.round((d.net / maxNet) * 100);
              const isSelected = d.month === selectedMonth;
              return (
                <div
                  key={d.month}
                  className="flex-1 flex flex-col items-center gap-2 cursor-pointer group"
                  onClick={() => setSelectedMonth(d.month)}
                >
                  <span className="text-xs text-[#9A9A9A] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    ${d.net.toLocaleString()}
                  </span>
                  <div
                    className={`w-full rounded-t-md transition-all ${isSelected ? 'bg-[#2C2C2C]' : 'bg-[#E8E6E3] group-hover:bg-[#8B7355]'}`}
                    style={{ height: `${heightPct}%` }}
                  ></div>
                  <span className={`text-xs whitespace-nowrap ${isSelected ? 'text-[#1A1A1A] font-semibold' : 'text-[#9A9A9A]'}`}>
                    {d.month.split(' ')[0]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Month Detail */}
        <div className="bg-white border border-[#E8E6E3] rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-[0.1em]">
              {selectedMonth}
            </h3>
            <span className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">{selected.status}</span>
          </div>
          <div className="flex flex-col gap-3">
            {[
              { label: 'Gross Rent', value: `$${selected.rent.toLocaleString()}`, positive: true },
              { label: 'Management Fee (10%)', value: `-$${selected.fees.toLocaleString()}`, positive: false },
              { label: 'Maintenance', value: selected.maintenance > 0 ? `-$${selected.maintenance.toLocaleString()}` : '$0', positive: selected.maintenance === 0 },
            ].map((item) => (
              <div key={item.label} className="flex justify-between items-center py-2.5 border-b border-[#F0EDE9]">
                <span className="text-xs text-[#6B6B6B]">{item.label}</span>
                <span className={`text-sm font-medium ${item.positive ? 'text-[#1A1A1A]' : 'text-red-500'}`}>{item.value}</span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-2">
              <span className="text-sm font-semibold text-[#1A1A1A]">Net Income</span>
              <span className="text-lg font-semibold text-emerald-600">${selected.net.toLocaleString()}</span>
            </div>
          </div>
          <button className="mt-5 w-full flex items-center justify-center gap-2 py-2.5 border border-[#E8E6E3] hover:border-[#8B7355] rounded-md text-xs text-[#6B6B6B] hover:text-[#8B7355] transition-all cursor-pointer">
            <i className="ri-download-line"></i>
            Download Statement
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E8E6E3] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E8E6E3]">
          <h3 className="text-sm font-semibold text-[#1A1A1A] uppercase tracking-[0.1em]">Transaction History</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAFAF8]">
                {['Month', 'Gross Rent', 'Mgmt Fee', 'Maintenance', 'Net Income', 'Status'].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-[#9A9A9A] uppercase tracking-[0.1em] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EDE9]">
              {[...monthlyData].reverse().map((row) => (
                <tr key={row.month} className="hover:bg-[#FAFAF8] transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-[#1A1A1A] whitespace-nowrap">{row.month}</td>
                  <td className="px-6 py-4 text-sm text-[#2C2C2C] whitespace-nowrap">${row.rent.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-red-500 whitespace-nowrap">-${row.fees.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-[#2C2C2C] whitespace-nowrap">{row.maintenance > 0 ? `-$${row.maintenance.toLocaleString()}` : '—'}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-emerald-600 whitespace-nowrap">${row.net.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
